import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Grid,
  Chip,
  Tabs,
  Tab,
  ClickAwayListener,
  InputAdornment,
  TextField,
  OutlinedInput
} from '@mui/material';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { fetchApi } from '../services/api';

interface SearchData {
  도시번호: number;
  도시한글명: string;
  국가번호: number;
  국가한글명: string;
  코드명: string;
  업체명: string;
  업체번호: number;
}

interface ApiResponse {
  table: SearchData[];
}

interface Country {
  name: string;
  code: number;
}

interface City {
  name: string;
  code: string;
}

interface Company {
  업체명: string;
  업체번호: number;
}

interface SearchLayerProps {
  isSelectOpen: boolean;
  setIsSelectOpen: (open: boolean) => void;
  handleSearchProp: (국가번호: number | undefined, 도시번호: number | undefined, 업체번호: number | undefined) => void;
}

const SearchLayer: React.FC<SearchLayerProps> = ({
  isSelectOpen,
  setIsSelectOpen,
  handleSearchProp,
}) => {
  const [searchData, setSearchData] = useState<SearchData[]>([]);
  const [companySearchTerm, setCompanySearchTerm] = useState<string>('');
  const [selectedRegion, setSelectedRegion] = useState<string>('');
  const [regions, setRegions] = useState<string[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [companies, setCompanies] = useState<SearchData[]>([]);
  const [filteredCompanies, setFilteredCompanies] = useState<SearchData[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [selectedCities, setSelectedCities] = useState<City[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [isSearchEnabled, setIsSearchEnabled] = useState<boolean>(false);
  const [currentTab, setCurrentTab] = useState(0);

  useEffect(() => {
    setIsSearchEnabled(selectedCities.length > 0 || selectedCompany !== '');
  }, [selectedCities, selectedCompany]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const CompanyNo = localStorage.getItem('CompanyNo');
        if (!CompanyNo) return;

        const postData = [
          { "컬럼명": "업체번호", "값": CompanyNo }
        ];

        const response = await fetchApi<ApiResponse>("ITN_S_업체별국가도시", postData);
        if (response.data?.table) {
          setSearchData(response.data.table);

          // Extract unique regions from 코드명
          const uniqueRegions = Array.from(
            new Set(response.data.table.map(item => item.코드명))
          ).filter(Boolean).sort();
          setRegions(uniqueRegions);

          // Extract unique countries
          const uniqueCountries = Array.from(
            new Set(response.data.table.map(item => item.국가번호))
          ).map(code => {
            const country = response.data.table.find(item => item.국가번호 === code);
            return {
              name: country!.국가한글명,
              code: code
            };
          });
          setCountries(uniqueCountries);

          // Extract unique cities
          const uniqueCities = Array.from(
            new Set(response.data.table.map(item => item.도시번호))
          ).map(code => {
            const city = response.data.table.find(item => item.도시번호 === code);
            return {
              name: city!.도시한글명,
              code: code.toString()
            };
          });
          setCities(uniqueCities);

          // Set companies
          const uniqueCompanies = response.data.table.filter((item, index, self) =>
            index === self.findIndex((t) => t.업체번호 === item.업체번호)
          );
          setCompanies(uniqueCompanies);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    const filtered = companies.filter(company =>
      company.업체명.toLowerCase().includes(companySearchTerm.toLowerCase())
    );
    setFilteredCompanies(filtered);
  }, [companySearchTerm, companies]);

  const handleRegionSelect = (region: string) => {
    if (selectedRegion === region) {
      setSelectedRegion('');
      setSelectedCountry(null);
      setSelectedCities([]);
      setSelectedCompany('');

      // Reset to show all options
      const uniqueCountries = Array.from(
        new Set(searchData.map(item => item.국가번호))
      ).map(code => {
        const country = searchData.find(item => item.국가번호 === code);
        return {
          name: country!.국가한글명,
          code: code
        };
      });
      setCountries(uniqueCountries);

      const uniqueCities = Array.from(
        new Set(searchData.map(item => item.도시번호))
      ).map(code => {
        const city = searchData.find(item => item.도시번호 === code);
        return {
          name: city!.도시한글명,
          code: code.toString()
        };
      });
      setCities(uniqueCities);

      const uniqueCompanies = searchData.filter((item, index, self) =>
        index === self.findIndex((t) => t.업체번호 === item.업체번호)
      );
      setCompanies(uniqueCompanies);
      return;
    }

    setSelectedRegion(region);
    setSelectedCountry(null);
    setSelectedCities([]);
    setSelectedCompany('');

    // Filter compatible items
    const compatibleData = searchData.filter(item => item.코드명 === region);

    const filteredCountries = Array.from(
      new Set(compatibleData.map(item => item.국가번호))
    ).map(code => {
      const country = compatibleData.find(item => item.국가번호 === code);
      return {
        name: country!.국가한글명,
        code: code
      };
    });

    setCountries(filteredCountries);

    const filteredCities = Array.from(
      new Set(compatibleData.map(item => item.도시번호))
    ).map(code => {
      const city = compatibleData.find(item => item.도시번호 === code);
      return {
        name: city!.도시한글명,
        code: code.toString()
      };
    });
    setCities(filteredCities);

    const filteredCompanies = compatibleData.filter((item, index, self) =>
      index === self.findIndex((t) => t.업체번호 === item.업체번호)
    );
    setCompanies(filteredCompanies);
  };

  const handleCountrySelect = (country: Country | null) => {
    setSelectedCountry(country);
    setSelectedCities([]);
    setSelectedCompany('');

    if (country) {
      const compatibleData = searchData.filter(item =>
        item.국가번호 === country.code &&
        (selectedRegion ? item.코드명 === selectedRegion : true)
      );

      const filteredCities = Array.from(
        new Set(compatibleData.map(item => item.도시번호))
      ).map(code => {
        const city = compatibleData.find(item => item.도시번호 === code);
        return {
          name: city!.도시한글명,
          code: code.toString()
        };
      });
      setCities(filteredCities);

      const filteredCompanies = compatibleData.filter((item, index, self) =>
        index === self.findIndex((t) => t.업체번호 === item.업체번호)
      );
      setCompanies(filteredCompanies);
    } else {
      // Reset to show all compatible options
      const compatibleData = selectedRegion
        ? searchData.filter(item => item.코드명 === selectedRegion)
        : searchData;

      const filteredCities = Array.from(
        new Set(compatibleData.map(item => item.도시번호))
      ).map(code => {
        const city = compatibleData.find(item => item.도시번호 === code);
        return {
          name: city!.도시한글명,
          code: code.toString()
        };
      });
      setCities(filteredCities);

      const filteredCompanies = compatibleData.filter((item, index, self) =>
        index === self.findIndex((t) => t.업체번호 === item.업체번호)
      );
      setCompanies(filteredCompanies);
    }
  };

  const handleCitySelect = (city: City | null) => {
    setSelectedCities(city ? [city] : []);
    setSelectedCompany('');

    if (city) {
      const compatibleData = searchData.filter(item =>
        item.도시번호.toString() === city.code &&
        (selectedCountry ? item.국가번호 === selectedCountry.code : true) &&
        (selectedRegion ? item.코드명 === selectedRegion : true)
      );

      const filteredCompanies = compatibleData.filter((item, index, self) =>
        index === self.findIndex((t) => t.업체번호 === item.업체번호)
      );
      setCompanies(filteredCompanies);
    } else {
      // Reset to show all compatible companies
      const compatibleData = searchData.filter(item =>
        (selectedCountry ? item.국가번호 === selectedCountry.code : true) &&
        (selectedRegion ? item.코드명 === selectedRegion : true)
      );

      const filteredCompanies = compatibleData.filter((item, index, self) =>
        index === self.findIndex((t) => t.업체번호 === item.업체번호)
      );
      setCompanies(filteredCompanies);
    }
  };

  const handleSearch = () => {
    const selectedCompanyData = companies.find(company => company.업체명 === selectedCompany);

    handleSearchProp(
      selectedCountry?.code,
      selectedCities[0]?.code ? Number(selectedCities[0].code) : undefined,
      selectedCompanyData?.업체번호
    );

    setIsSelectOpen(false);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  return (
    <ClickAwayListener
      onClickAway={(event) => {
        // Select가 열려있을 때만 동작
        if (!isSelectOpen) return;

        const menuElement = document.querySelector('.MuiMenu-paper');
        const selectElement = document.querySelector('.MuiSelect-select');
        const menuContentElement = document.querySelector('.MuiMenu-list');

        // Select, 메뉴, 또는 메뉴 내부 컨텐츠를 클릭한 경우는 무시
        if (menuElement?.contains(event.target as Node) ||
          selectElement?.contains(event.target as Node) ||
          menuContentElement?.contains(event.target as Node) ||
          (event.target as Element).closest('.MuiMenu-paper')) {
          return;
        }

        setIsSelectOpen(false);
      }}
      mouseEvent="onMouseDown"
    >
      <FormControl fullWidth sx={{
        '& .MuiInputBase-root': {
          borderRadius: '8px',
          backgroundColor: '#EEEEEE',
          minWidth: { sm: 300, xs: 200 },
          '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
        },
        '& .MuiSelect-select': { fontSize: '13px' },
        '& .MuiSelect-icon': { color: '#757575' }
      }}>
        <Select
          value=""
          displayEmpty
          open={isSelectOpen}
          onClose={(e: React.SyntheticEvent) => {
            if ((e as any).key === 'Escape') {
              setIsSelectOpen(false);
            }
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (!isSelectOpen) {
              setIsSelectOpen(true);
            }
          }}
          MenuProps={{
            // 메뉴 내부 클릭 이벤트가 상위로 전파되지 않도록 설정
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
            },
            PaperProps: {
              onClick: (e: React.MouseEvent) => {
                e.stopPropagation();
              },
              sx: {
                height: 'calc(100vh - 44px)',
                width: '500px',
                '& .MuiList-root': {
                  padding: 2,
                  height: '100%'
                },
              },
            }
          }}
          IconComponent={SearchOutlinedIcon}
          renderValue={() => {
            const selections = [];
            if (selectedCountry) selections.push(selectedCountry.name);
            if (selectedCities.length > 0) selections.push(selectedCities[0].name);
            if (selectedCompany) selections.push(selectedCompany);
            return selections.length > 0 ?
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selections.map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </Box> :
              <em style={{ fontSize: '13px' }}>국가/도시/업체 선택</em>;
          }}
          input={<OutlinedInput />}
        >
          <Box sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* 좌우 2단 레이아웃 */}
            <Box sx={{
              display: 'flex',
              flex: 1,
              gap: 1.5,
              p: 0,
              height: '100%'
            }}>
              {/* 왼쪽: 지역/국가/도시 */}
              <Box sx={{
                width: '65%',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                height: '100%'
              }}>
                {/* 지역 선택 */}
                <Box sx={{ height: 'calc(22%)' }}>
                  <Typography variant="subtitle2" sx={{
                    mb: 0.5,
                    fontWeight: 600,
                    color: 'text.secondary',
                  }}>
                    지역
                  </Typography>
                  <Box sx={{
                    p: 1.5,
                    bgcolor: '#f5f5f5',
                    borderRadius: 1,
                    height: 'calc(100% - 35px)',
                    overflowY: 'auto'
                  }}>
                    <Grid container spacing={1}>
                      {regions.map((region) => (
                        <Grid item xs={4} key={region}>
                          <Button
                            variant={selectedRegion === region ? "contained" : "text"}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRegionSelect(region);
                            }}
                            size="small"
                            sx={{
                              width: '100%',
                              fontSize: '12px',
                              height: '28px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              color: selectedRegion === region ? 'white' : 'inherit',
                              '&:hover': {
                                backgroundColor: selectedRegion === region ? 'primary.main' : 'rgba(0, 0, 0, 0.04)'
                              }
                            }}
                          >
                            {region || '지역 없음'}
                          </Button>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Box>

                {/* 국가 선택 */}
                <Box sx={{ height: 'calc(26% - 10px)' }}>
                  <Typography variant="subtitle2" sx={{
                    mb: 0.5,
                    fontWeight: 600,
                    color: 'text.secondary'
                  }}>
                    국가
                  </Typography>
                  <Box sx={{
                    p: 1.5,
                    bgcolor: '#f5f5f5',
                    borderRadius: 1,
                    height: 'calc(100% - 35px)',
                    overflowY: 'auto'
                  }}>
                    <Grid container spacing={1}>
                      {countries.map((country) => (
                        <Grid item xs={4} key={country.code}>
                          <Button
                            variant={selectedCountry?.code === country.code ? "contained" : "text"}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCountrySelect(selectedCountry?.code === country.code ? null : country);
                            }}
                            size="small"
                            sx={{
                              width: '100%',
                              fontSize: '12px',
                              height: '28px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              color: selectedCountry?.code === country.code ? 'white' : 'inherit',
                              '&:hover': {
                                backgroundColor: selectedCountry?.code === country.code ? 'primary.main' : 'rgba(0, 0, 0, 0.04)'
                              }
                            }}
                          >
                            {country.name || '국가 없음'}
                          </Button>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Box>

                {/* 도시 선택 */}
                <Box sx={{ height: 'calc(50%)' }}>
                  <Typography variant="subtitle2" sx={{
                    mb: 0.5,
                    fontWeight: 600,
                    color: 'text.secondary'
                  }}>
                    도시
                  </Typography>
                  <Box sx={{
                    p: 1.5,
                    bgcolor: '#f5f5f5',
                    borderRadius: 1,
                    height: 'calc(100% - 35px)',
                    overflowY: 'auto'
                  }}>
                    <Grid container spacing={1}>
                      {cities.map((city) => (
                        <Grid item xs={4} key={city.code}>
                          <Button
                            variant={selectedCities.some(c => c.code === city.code) ? "contained" : "text"}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCitySelect(selectedCities.some(c => c.code === city.code) ? null : city);
                            }}
                            size="small"
                            sx={{
                              width: '100%',
                              fontSize: '12px',
                              height: '28px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              color: selectedCities.some(c => c.code === city.code) ? 'white' : 'inherit',
                              '&:hover': {
                                backgroundColor: selectedCities.some(c => c.code === city.code) ? 'primary.main' : 'rgba(0, 0, 0, 0.04)'
                              }
                            }}
                          >
                            {city.name || '도시 없음'}
                          </Button>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Box>

                {/* 검색 버튼을 여기로 이동 */}
                <Box sx={{
                  display: 'flex',
                  mt: 'auto',
                }}>
                  <Button
                    variant="contained"
                    onClick={handleSearch}
                    disabled={!isSearchEnabled}
                    startIcon={<SearchOutlinedIcon />}
                    sx={{
                      width: '100%',
                      height: '42px',
                      bgcolor: '#2D2D2D',
                      '&:hover': {
                        bgcolor: '#1a1a1a',
                      }
                    }}
                  >
                    {isSearchEnabled ? '일정표 검색' : '도시/업체 선택'}
                  </Button>
                </Box>
              </Box>

              {/* 오른쪽: 업체 선택만 남김 */}
              <Box sx={{
                width: '35%',
                display: 'flex',
                flexDirection: 'column',
                gap: 1
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" sx={{ 
                    fontWeight: 600,
                    color: 'text.secondary'
                  }}>
                    업체
                  </Typography>
                    <TextField
                      size="small"
                      placeholder="업체검색"
                      value={companySearchTerm}
                      onChange={(e) => setCompanySearchTerm(e.target.value)}
                      sx={{
                        ml: 1,
                        '& .MuiOutlinedInput-root': {
                          height: '32px',
                          fontSize: '13px',
                          backgroundColor: 'white',
                          width: '120px',
                        },
                      }}
                    />
                </Box>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{
                    p: 1.5,
                    bgcolor: '#f5f5f5',
                    borderRadius: 1,
                    flex: 1,
                    overflowY: 'auto',
                  }}>
                    <Grid container spacing={1}>
                      {filteredCompanies.map((company) => (
                        <Grid item xs={12} key={company.업체번호}>
                          <Button
                            variant={selectedCompany === company.업체명 ? "contained" : "text"}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCompany(selectedCompany === company.업체명 ? '' : company.업체명);
                            }}
                            size="small"
                            sx={{
                              width: '100%',
                              fontSize: '12px',
                              height: '32px',
                              justifyContent: 'flex-start',
                              px: 2,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              color: selectedCompany === company.업체명 ? 'white' : 'inherit',
                              '&:hover': {
                                backgroundColor: selectedCompany === company.업체명 ? 'primary.main' : 'rgba(0, 0, 0, 0.04)'
                              }
                            }}
                          >
                            {company.업체명 || '업체 없음'}
                          </Button>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Box>

                <Button
                  variant="outlined"
                  onClick={() => setIsSelectOpen(false)}
                  sx={{
                    height: '42px',
                    width: '100%',
                    borderColor: '#2D2D2D',
                    color: '#2D2D2D',
                    '&:hover': {
                      borderColor: '#1a1a1a',
                      bgcolor: 'rgba(0, 0, 0, 0.04)',
                    }
                  }}
                >
                  닫기
                </Button>
              </Box>
            </Box>
          </Box>
        </Select>
      </FormControl>
    </ClickAwayListener>
  );
};

export default SearchLayer;
